import type { SelectProps } from '@risk-smart/themed-cloudscape-components/select';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { WizardStatus } from '@risksmart-app/domain/src/types/consts';
import { AssessmentActivityStatus as Assessment_Activity_Status_Enum } from '@risksmart-app/domain/src/types/consts/assessment-activity-status';
import { AssessmentActivityType as Assessment_Activity_Type_Enum } from '@risksmart-app/domain/src/types/consts/assessment-activity-type';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { Dispatch, FC, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalForm } from 'src/components/form/form/ModalForm';
import { useGetAssessmentsRegister } from 'src/hooks/queries';

import { useInsertAssessmentActivityWithLinkedItems } from '@/hooks/mutations/assessment-activity/useInsertAssessmentActivityWithLinkedItems';
import { useUpdateAssessmentActivityWithLinkedItems } from '@/hooks/mutations/assessment-activity/useUpdateAssessmentActivityWithLinkedItems';
import { useInsertWizard, useUpdateWizard } from '@/hooks/mutations/wizard';
import { handleError } from '@/utils/errorUtils';

import { LinkAssessmentForm } from '../forms/LinkAssessmentForm';
import type { LinkAssessmentFormFields } from '../forms/LinkAssessmentFormSchema';
import { LinkAssessmentFormSchema } from '../forms/LinkAssessmentFormSchema';
import { useNavigateToStep } from '../hooks/useNavigateToStep';
import { useWizardStore } from '../store/useWizardStore';

type LinkAssessmentModalProps = {
  isVisible: boolean;
  setIsVisible: Dispatch<SetStateAction<boolean>>;
  setIsCreateAssessmentModalVisible: Dispatch<SetStateAction<boolean>>;
  basePath: string;
  riskId: string;
  riskTitle: string;
};

export const LinkAssessmentModal: FC<LinkAssessmentModalProps> = ({
  isVisible,
  setIsVisible,
  setIsCreateAssessmentModalVisible,
  basePath,
  riskId,
  riskTitle,
}) => {
  const { addNotification } = useNotifications();
  const { t } = useTranslation(['common'], { keyPrefix: 'wizard' });
  const { navigateToStep } = useNavigateToStep();
  const { user } = useRisksmartUser();
  const {
    setAssessmentId,
    setActivityId,
    wizardStatus,
    assessmentId,
    activityId,
    setWizardStatus,
    currentStep,
  } = useWizardStore();

  const { insertWizard } = useInsertWizard();
  const { updateWizard } = useUpdateWizard();

  const { insertAssessmentActivity } =
    useInsertAssessmentActivityWithLinkedItems();
  const { updateAssessmentActivity } =
    useUpdateAssessmentActivityWithLinkedItems();

  const { data, error, loading } = useGetAssessmentsRegister({ queryArgs: {} });

  if (error && !loading) {
    setIsVisible(false);
  }

  const onSave = async (value: LinkAssessmentFormFields) => {
    if (wizardStatus === WizardStatus.Planned) {
      await updateWizard({
        object: {
          RiskId: riskId,
          CurrentStep: 0,
          Status: WizardStatus.InProgress,
        },
      });
      navigateToStep(basePath, currentStep);
      setWizardStatus(WizardStatus.InProgress);
      await updateAssessmentActivity({
        Id: activityId,
        Status: Assessment_Activity_Status_Enum.InProgress,
        ActivityType: Assessment_Activity_Type_Enum.Task,
        // COI-683: the risk↔activity relationship is the `RiskId` column; see
        // the insert branch below.
        LinkedItemIds: [],
        ParentId: assessmentId,
        IsWizardAction: true,
        OwnerUserIds: [user!.userId],
        OwnerGroupIds: [],
      });

      return;
    }
    const insertResult = await insertAssessmentActivity({
      Title: `${t('wizardName')} - ${riskTitle}`,
      Status: Assessment_Activity_Status_Enum.InProgress,
      ActivityType: Assessment_Activity_Type_Enum.Task,
      Summary: `${t('wizardName')} - ${riskTitle}`,
      // COI-683: `RiskId` below is now the relationship — migration
      // 1786699284000 projects it into `linked_item` as a grant-carrying
      // `parent_child` row, which is what lets the risk's owner reach this
      // activity. This is the entry path that produced the reported bug: it
      // attaches a risk to an EXISTING assessment and never set
      // `OriginatingItemId`, so no ancestry was ever minted.
      LinkedItemIds: [],
      ParentId: value.AssessmentId,
      IsRCSA: true,
      RiskId: riskId,
      OwnerUserIds: [user!.userId],
      OwnerGroupIds: [],
    });

    if (!insertResult?.insertAssessmentActivityWithLinkedItems?.Id) {
      throw new Error('Assessment Activity id is missing');
    }

    try {
      const result = await insertWizard({
        object: {
          RiskId: riskId,
          AssessmentId: value.AssessmentId,
          Status: WizardStatus.InProgress,
          ActivityId: insertResult?.insertAssessmentActivityWithLinkedItems.Id,
        },
      });
      if (!result.insertChildWizard?.RiskId) {
        addNotification({
          type: 'error',
          content: <>{'Wizard already exists for this risk'}</>,
        });

        return;
      }
      setWizardStatus(WizardStatus.InProgress);
      navigateToStep(basePath, currentStep);
      setAssessmentId(value.AssessmentId);
      setActivityId(
        insertResult?.insertAssessmentActivityWithLinkedItems?.Id ?? ''
      );
    } catch (error) {
      handleError(error);
      addNotification({
        type: 'error',
        content: (
          <>{error instanceof Error ? error.message : 'Unknown error'}</>
        ),
      });
    }
  };

  const onDismiss = async () => {
    setIsVisible(false);
  };

  const createAssessment = async () => {
    setIsVisible(false);
    setIsCreateAssessmentModalVisible(true);
  };

  const assessmentOptions: (SelectProps.Option & { id: string })[] | undefined =
    data?.assessment.map((assessment) => {
      return {
        id: assessment.Id,
        value: assessment.Id,
        label: assessment.Title,
        description: assessment.Summary ?? undefined,
      };
    });

  return (
    <ModalForm
      values={{ AssessmentId: assessmentId }}
      i18n={{
        entity_name: t('forms.entityName'),
        edit_modal_title: t('forms.linkModalTitle'),
        create_modal_title: t('forms.linkModalTitle'),
      }}
      testId={'linkToAnAssessmentModal'}
      onSave={onSave}
      onDismiss={onDismiss}
      defaultValues={{ AssessmentId: '' }}
      schema={LinkAssessmentFormSchema}
      formId={'link-assessment-form'}
      visible={isVisible}
      secondaryActions={
        wizardStatus === WizardStatus.Planned
          ? []
          : [
              {
                label: t('forms.createAssessmentButton'),
                action: createAssessment,
              },
            ]
      }
      parentType={Parent_Type_Enum.Assessment}
    >
      <LinkAssessmentForm
        assessmentOptions={
          (wizardStatus === WizardStatus.Planned
            ? assessmentOptions?.filter(
                (assessmentOption) => assessmentOption.id === assessmentId
              )
            : assessmentOptions) ?? []
        }
      ></LinkAssessmentForm>
    </ModalForm>
  );
};
